import { Edge, NodeType } from '@weave/schema-gen-ts/dist/schemas/phone/callroute/v1/callroute_service.pb';
import {
  ListResponse as OverridesListResponse,
  PhoneOverride,
} from '@weave/schema-gen-ts/dist/schemas/phone/override/v2/override_service.pb';
import { CallRouteV1 } from '@frontend/api-call-route';
import { genUUIDV4 } from '@frontend/string';
import { isNodeStateOfType, NodeState } from '../../call-route-details/add-step-panel/types';
import { associativeNodes, extendableNodes } from '../../call-route-details/call-route-flow/data';
import { NodeAction, NodeActionType } from '../../types';
import { logError } from '../utils';

/**
 * Helper function used to determine if a node is a descendent of a phone tree node.
 * NOTE: if the node is a phone tree node, it is not considered a descendent of itself.
 *
 * @param nodeId The id of the node to be considered if it is a descendent of a phone tree node.
 * @param nodes The nodes in the call route.
 * @param edges The edges in the call route.
 * @returns
 */
export const isPhoneTreeDescendant = (nodeId: string, nodes: CallRouteV1.Types.Node[], edges: Edge[]): boolean =>
  !!findAncestorOfNodeType(CallRouteV1.Types.NodeType.NODE_TYPE_PHONE_TREE, nodeId, nodes, edges);

/**
 * Helper function used to determine if a node is a descendent of a phone hours node.
 * NOTE: if the node is a phone hours node, it is not considered a descendent of itself.
 *
 * @param nodeId The id of the node to be considered if it is a descendent of a phone hours node.
 * @param nodes The nodes in the call route.
 * @param edges The edges in the call route.
 * @returns
 */
export const isPhoneHoursDescendant = (nodeId: string, nodes: CallRouteV1.Types.Node[], edges: Edge[]): boolean =>
  !!findAncestorOfNodeType(CallRouteV1.Types.NodeType.NODE_TYPE_PHONE_HOURS, nodeId, nodes, edges);

/**
 * Helper function used to determine if a node is a descendent of a Weave Assistant node.
 * NOTE: if the node is a Weave Assistant node, it is not considered a descendent of itself.
 *
 * @param nodeId The id of the node to be considered if it is a descendent of a Weave Assistant node.
 * @param nodes The nodes in the call route.
 * @param edges The edges in the call route.
 * @returns
 */
export const isWeaveAssistantDescendant = (nodeId: string, nodes: CallRouteV1.Types.Node[], edges: Edge[]): boolean =>
  !!findAncestorOfNodeType(CallRouteV1.Types.NodeType.NODE_TYPE_WEAVE_ASSISTANT, nodeId, nodes, edges);

/**
 * Helper function used to determine if a node is an ancestor of a Weave Assistant node.
 * In other words, checks if any descendant of the given node is a Weave Assistant node.
 * NOTE: if the node is a Weave Assistant node, it is not considered an ancestor of itself.
 *
 * @param nodeId The id of the node to check.
 * @param nodes The nodes in the call route.
 * @param edges The edges in the call route.
 * @returns true if the node has a Weave Assistant descendant, false otherwise.
 */
export const isWeaveAssistantAncestor = (nodeId: string, nodes: CallRouteV1.Types.Node[], edges: Edge[]): boolean =>
  !!findDescendantOfNodeType(CallRouteV1.Types.NodeType.NODE_TYPE_WEAVE_ASSISTANT, nodeId, nodes, edges);

/**
 * Helper function used to determine if a node is an ancestor of a Phone Hours node.
 * In other words, checks if any descendant of the given node is a Phone Hours node.
 * NOTE: if the node is a Phone Hours node, it is not considered an ancestor of itself.
 *
 * @param nodeId The id of the node to check.
 * @param nodes The nodes in the call route.
 * @param edges The edges in the call route.
 * @returns true if the node has a Phone Hours descendant, false otherwise.
 */
export const isPhoneHoursAncestor = (nodeId: string, nodes: CallRouteV1.Types.Node[], edges: Edge[]): boolean =>
  !!findDescendantOfNodeType(CallRouteV1.Types.NodeType.NODE_TYPE_PHONE_HOURS, nodeId, nodes, edges);

/**
 * Determines if a given node type is a terminal node.
 *
 * A terminal node is defined as a node type that is not extendable (can not add downstream steps to it).
 *
 * @param nodeType - The type of the node to check. If not provided, an error is logged and the function returns false.
 * @returns `true` if the node type is a terminal node, `false` otherwise.
 */
export const isTerminalNode = (node: NodeState): boolean => {
  const nodeType = node.type;

  // Forwarding number nodes are considered terminal nodes if the timeout is set to 0 (ring until answered).
  if (isNodeStateOfType(node, CallRouteV1.Types.NodeType.NODE_TYPE_FORWARD_NUMBER)) {
    const isRingUntilAnswered = node.callObject.forwardingNumberExpansion.timeout === 0;

    if (isRingUntilAnswered) {
      return true;
    }
  }

  return !extendableNodes.includes(nodeType);
};

export const isAssociativeNode = (nodeType?: string) => {
  if (!nodeType) {
    logError('Node type is missing: PC-739odkw', 'isAssociativeNode', { nodeType });
    return false;
  }

  return associativeNodes.includes(nodeType);
};

/**
 * Helper function that will return true if the node with the given nodeId has any downstream steps.
 * Downstream steps are considered to be the direct children of the node if the node is a non-associative node.
 * If the node is an associative node, then the downstream steps are considered to be the grandchildren of the node.
 * Since the direct children of an associative node are meta nodes such as dialOptions, "fallback", "open", "closed" and "break" nodes.
 *
 * @param {Object} params - The parameters for the function.
 * @param {string} params.nodeId - The ID of the node to check.
 * @param {Node[]} params.nodes - The list of all nodes.
 * @param {Edge[]} params.edges - The list of all edges.
 * @returns {boolean} - Returns true if the node has downstream steps, otherwise false.
 */
export const hasDownStreamSteps = ({
  nodeId,
  nodes,
  edges,
}: {
  nodeId: string;
  nodes: CallRouteV1.Types.Node[];
  edges: Edge[];
}): boolean => {
  const node = nodes.find((n) => n.id === nodeId);
  if (!node) {
    logError('Could not find node in hasDownStreamSteps: PC-jd839k', 'hasDownStreamSteps', {
      nodeId,
      nodes: nodes.map((n) => n.id),
    });
    return false;
  }

  const isAssociative = isAssociativeNode(node.type);

  let hasDownStreamSteps = false;
  if (isAssociative) {
    hasDownStreamSteps = hasGrandchildren({ nodeId, nodes, edges });
  } else {
    hasDownStreamSteps = getDescendantNodes({ nodeId, nodes, edges }).length > 0;
  }

  return hasDownStreamSteps;
};

/**
 * Determines if a node has grandchildren in a graph.
 *
 * Grandchildren are considered to be the children of the children of the node. So this function will
 * return true if the node has at least one child node that itself has at least one child node.
 *
 * @param {Object} params - The parameters for the function.
 * @param {string} params.nodeId - The ID of the node to check for grandchildren.
 * @param {Node[]} params.nodes - The array of nodes in the graph.
 * @param {Edge[]} params.edges - The array of edges in the graph.
 * @returns {boolean} - Returns `true` if the node has grandchildren, otherwise `false`.
 */
export const hasGrandchildren = ({
  nodeId,
  nodes,
  edges,
}: {
  nodeId: string;
  nodes: CallRouteV1.Types.Node[];
  edges: Edge[];
}): boolean => {
  const node = nodes.find((n) => n.id === nodeId);
  if (!node) {
    return false;
  }

  const nodeEdges = edges.filter((edge) => edge.sourceId === nodeId);
  if (!nodeEdges.length) {
    return false;
  }

  const hasGrandchildren = nodeEdges.some((edge) => {
    const childNode = nodes.find((n) => n.id === edge.targetId);
    if (!childNode) {
      return false;
    }

    return edges.some((e) => e.sourceId === childNode.id);
  });

  return hasGrandchildren;
};

/**
 * Helper function that will retrieve the first ancestor node of type nodeType for a given node ID.
 * To find an ancestor node of the desired type, we need to traverse the callroute edges starting from the current
 * node as the target and work our way up to the root node. If we encounter a node with the specified type,
 * then return it.
 * NOTE: if the node is a type of nodeType, it is not considered an ancestor of itself.
 *
 * @param nodeId - The ID of the node for which to find the ancestor.
 * @param nodes - An array of all nodes.
 * @param edges - An array of all edges connecting the nodes.
 * @param isInitialCall - A flag indicating if this is the initial call to the function (used for recursion).
 * @returns The ancestor node of type nodeType, or `undefined` if not found.
 */
export const findAncestorOfNodeType = (
  nodeType: CallRouteV1.Types.NodeType,
  nodeId: string,
  nodes: CallRouteV1.Types.Node[],
  edges: Edge[],
  isInitialCall = true
): CallRouteV1.Types.Node | undefined => {
  const node = nodes.find((n) => n.id === nodeId);
  if (!node) {
    return undefined;
  }

  // Return undefined if the initial node is a node of the specified type.
  if (isInitialCall && node.type === nodeType) {
    return undefined;
  }

  const edge = edges.find((e) => e.targetId === nodeId);
  if (!edge) {
    return undefined;
  }

  if (node.type === nodeType) {
    return node;
  }

  // Pass false for subsequent recursive calls to indicate they're not the initial call
  return findAncestorOfNodeType(nodeType, edge.sourceId, nodes, edges, false);
};

/**
 * Helper function that will retrieve the first descendant node of type nodeType for a given node ID.
 * To find a descendant node of the desired type, we traverse the callroute edges starting from the current
 * node as the source and work our way down through the graph. If we encounter a node with the specified type,
 * then return it.
 * NOTE: if the node is a type of nodeType, it is not considered a descendant of itself.
 *
 * @param nodeType - The type of node to search for.
 * @param nodeId - The ID of the node for which to find the descendant.
 * @param nodes - An array of all nodes.
 * @param edges - An array of all edges connecting the nodes.
 * @returns The first descendant node of type nodeType, or `undefined` if not found.
 */
export const findDescendantOfNodeType = (
  nodeType: CallRouteV1.Types.NodeType,
  nodeId: string,
  nodes: CallRouteV1.Types.Node[],
  edges: Edge[]
): CallRouteV1.Types.Node | undefined => {
  const node = nodes.find((n) => n.id === nodeId);
  if (!node) {
    return undefined;
  }
  const descendants = getDescendantNodes({ nodeId, nodes, edges });
  return descendants.find((n) => n.type === nodeType);
};

/**
 * Helper function that will take a nodeId and return all the ancestors of that node that are phone tree nodes.
 * Recursively finds all ancestor phone tree nodes of a given node in a call route graph.
 * NOTE: The highest ancestor node is not included in the returned array (even if it is a phone tree node since the
 * highest node should be a start node).
 *
 * @param nodeId - The ID of the node for which to find phone tree ancestors.
 * @param nodes - An array of all nodes in the call route graph.
 * @param edges - An array of all edges representing connections between nodes in the call route graph.
 * @returns An array of ancestor nodes of type CallRouteV1.Types.NodeType.NODE_TYPE_PHONE_TREE
 */
export const findAllPhoneTreeAncestors = (
  nodeId: string,
  nodes: CallRouteV1.Types.Node[],
  edges: Edge[]
): CallRouteV1.Types.Node[] => {
  const node = nodes.find((n) => n.id === nodeId);
  if (!node) {
    return [];
  }

  const edge = edges.find((e) => e.targetId === nodeId);
  if (!edge) {
    return [];
  }

  const ancestors = findAllPhoneTreeAncestors(edge.sourceId, nodes, edges);

  if (node.type === CallRouteV1.Types.NodeType.NODE_TYPE_PHONE_TREE) {
    return [...ancestors, node];
  }

  return ancestors;
};

/**
 * Retrieves all descendant nodes of a given node in a graph.
 *
 * NOTE: This will return a new array containing references to the original node objects found in the nodes array
 * passed in. It does not create deep copies or new instances of the node objects themselves.
 *
 * @param {Object} params - The parameters for the function.
 * @param {string} params.nodeId - The ID of the node to start from.
 * @param {Node[]} params.nodes - The list of all nodes in the graph.
 * @param {Edge[]} params.edges - The list of all edges in the graph.
 * @param {string[]} [params.omitIds] - Optional list of node IDs to omit them and their descendants from the results.
 * @returns {Node[]} An array of descendant nodes.
 */
export const getDescendantNodes = ({
  nodeId,
  nodes,
  edges,
  omitIds,
}: {
  nodeId: string;
  nodes: CallRouteV1.Types.Node[];
  edges: Edge[];
  omitIds?: string[];
}): CallRouteV1.Types.Node[] => {
  const node = nodes.find((n) => n.id === nodeId);
  if (!node) {
    return [];
  }

  const nodeEdges = edges.filter((edge) => edge.sourceId === nodeId);
  if (!nodeEdges.length) {
    return [];
  }

  const descendants = nodeEdges.reduce<CallRouteV1.Types.Node[]>((acc, edge) => {
    const childNode = nodes.find((n) => n.id === edge.targetId);
    if (!childNode || (omitIds && omitIds.includes(childNode.id))) {
      return acc;
    }

    return [...acc, childNode, ...getDescendantNodes({ nodeId: childNode.id, nodes, edges })];
  }, []);

  return descendants;
};

/**
 * Retrieves the child nodes of a given phone tree node that represent dial options.
 * Will return the nodes that are direct children of a phone tree node that are type "TreeOption".
 *
 * @param phoneTreeNodeId - The ID of the phone tree node for which to find child nodes.
 * @param nodes - An array of all nodes in the call route graph.
 * @param edges - An array of all edges connecting nodes in the call route graph.
 * @returns An array of child nodes that are of type `TreeOption` and are connected to the given node.
 */
export const getDialOptionChildren = (
  phoneTreeNodeId: string,
  nodes: CallRouteV1.Types.Node[],
  edges: Edge[]
): CallRouteV1.Types.Node[] => {
  const nodeEdges = edges.filter((edge) => edge.sourceId === phoneTreeNodeId);
  if (!nodeEdges.length) {
    return [];
  }

  const dialOptions = nodeEdges.reduce<CallRouteV1.Types.Node[]>((acc, edge) => {
    const childNode = nodes.find((n) => n.id === edge.targetId);
    if (!childNode || childNode.type !== CallRouteV1.Types.NodeType.NODE_TYPE_TREE_OPTION) {
      return acc;
    }

    return [...acc, childNode];
  }, []);

  return dialOptions;
};

/**
 * Finds the root node in a graph structure defined by nodes and edges.
 * The root node is identified as the single node that has no incoming edges.
 *
 * @param nodes - An array of all nodes in the graph.
 * @param edges - An array of all edges connecting the nodes.
 * @returns The root node object if found; otherwise, `undefined`.
 */
export const getRootNode = (nodes: CallRouteV1.Types.Node[], edges: Edge[]): CallRouteV1.Types.Node | undefined => {
  const targetNodeIds = new Set(edges.map((edge) => edge.targetId));
  return nodes.find((node) => !targetNodeIds.has(node.id));
};

/**
 * Retrieves the names of sibling break hour nodes for a given break hour node within a call route graph.
 *
 * @param breakHourNodeId - The ID of the break hour node whose siblings are to be found.
 * @param nodes - The list of all nodes in the call route graph.
 * @param edges - The list of all edges representing connections between nodes in the call route graph.
 * @returns An array of names of sibling break hour nodes (excluding the given node). Returns an empty array if no parent is found.
 */
export const getSiblingBreakNames = (breakHourNodeId: string, nodes: CallRouteV1.Types.Node[], edges: Edge[]) => {
  const parentNodeId = edges.find((edge) => edge.targetId === breakHourNodeId)?.sourceId;
  if (!parentNodeId) {
    logError(`Could not find parent edge for break hour node: ${breakHourNodeId}`, 'getSiblingBreakNames', {
      breakHourNodeId,
      edgeTargetIds: edges.map((edge) => edge.targetId),
    });
    return [];
  }

  const siblingBreakHourNodes = edges
    // Get all edges that connect to the parent node and filter out the current break hour node (we don't need it's name)
    .filter((edge) => edge.sourceId === parentNodeId && edge.targetId !== breakHourNodeId)
    // Get the target nodes of the edges
    .map((edge) => nodes.find((node) => node.id === edge.targetId))
    // Filter out any nodes that are not break hour nodes
    .filter(
      (node) => node && node.type === CallRouteV1.Types.NodeType.NODE_TYPE_BREAK_HOURS
    ) as CallRouteV1.Types.Node[];

  // Get the names of the sibling break hour nodes
  const siblingBreakNames = siblingBreakHourNodes.map((node) => node.callObject.primitiveName);

  return siblingBreakNames;
};

/**
 * Extracts a subgraph from a given set of nodes and edges, starting from a specified root node.
 *
 * This function first locates the root node using either its `id` or `callObject.primitiveId`,
 * based on the `identifierMode`. It then traverses the graph to find all descendant nodes
 * and the edges that connect the nodes within the subgraph.
 *
 * @param nodes - The array of all nodes in the graph.
 * @param edges - The array of all edges in the graph.
 * @param rootNodeIdentifier - The unique identifier of the root node for the subgraph.
 * @param identifierMode - Specifies whether to find the root node by its `id` ('byId') or by its `callObject.primitiveId` ('byPrimitiveId').
 * @returns An object containing the `rootNode`, `descendantNodes`, and `descendantEdges` of the subgraph,
 * or `undefined` if the root node could not be found.
 */
export const extractSubGraph = (
  nodes: CallRouteV1.Types.Node[],
  edges: Edge[],
  rootNodeIdentifier: string,
  identifierMode: 'byId' | 'byPrimitiveId'
) => {
  const rootNode =
    identifierMode === 'byId'
      ? nodes.find((node) => node.id === rootNodeIdentifier)
      : nodes.find((node) => node.callObject?.primitiveId === rootNodeIdentifier);

  if (!rootNode) {
    return undefined;
  }

  const descendantNodes = getDescendantNodes({ nodeId: rootNode.id, nodes: nodes, edges: edges });
  const relevantNodeIds = new Set(descendantNodes.map((node) => node.id)); // A set of unique node IDs for quick lookup
  const descendantEdges = edges.filter(
    (edge) =>
      (relevantNodeIds.has(edge.sourceId) && relevantNodeIds.has(edge.targetId)) ||
      (edge.sourceId === rootNode.id && relevantNodeIds.has(edge.targetId))
  );

  return { rootNode, descendantNodes, descendantEdges };
};

/**
 * Takes a string in the format 'Repeats "{variableName}"'
 * and returns it in the format 'Repeats "Copy of {variableName}"'.
 * * @param inputString The string to transform.
 * @returns The transformed string.
 */
function transformRepeatsString(inputString: string): string {
  // The regular expression:
  // ^: asserts the start of the string
  // (Repeats\s+): captures the literal "Repeats" followed by one or more spaces (Group 1)
  // (['"“]) : captures the opening quote (single, double, or smart double quote) (Group 2)
  // (.*?) : captures the variable name (non-greedily) (Group 3)
  // (['"”]) : captures the closing quote (single, double, or smart double quote) (Group 4)
  // $: asserts the end of the string
  const regex = /^(Repeats\s+)(['"“])(.*?)((['"”]))$/;

  // The replacement string:
  // $1: Repeats and space
  // $2: Opening quote
  // Copy of : The new prefix
  // $3: The captured variable name
  // $4: The closing quote
  const replacement = '$1$2Copy of $3$4';

  // Check if the input string matches the required format
  if (regex.test(inputString)) {
    return inputString.replace(regex, replacement);
  } else {
    // Handle cases where the string doesn't match the expected format
    console.warn(`Input string "${inputString}" does not match the expected format.`);
    return inputString;
  }
}

type DroppedRepeatNode = {
  id: string;
  name: string;
};

/**
 * Sanitizes a set of nodes and edges for a duplicated phone tree segment.
 *
 * @param nodes The array of nodes to be duplicated.
 * @param edges The array of edges connecting the nodes to be duplicated.
 * @returns An object containing the sanitized nodes and edges ready for duplication, and a list of
 *          any Repeat nodes that were dropped because they pointed outside the duplicated subtree.
 */
export const sanitizeNodesForDuplicatePhoneTree = (
  nodes: CallRouteV1.Types.Node[],
  edges: Edge[]
): {
  sanitized: { nodes: CallRouteV1.Types.Node[]; edges: Edge[] };
  droppedRepeatNodes: DroppedRepeatNode[];
} => {
  if (!nodes.length) {
    return { sanitized: { nodes, edges }, droppedRepeatNodes: [] };
  }

  // Create a Map of old node Ids to new node Ids. We will need these to update the duplicated
  // nodes and edges since all node Ids must be unique.
  const oldIdsToNewIds = new Map<string, string>();
  nodes.forEach((n) => {
    const newId = genUUIDV4();
    oldIdsToNewIds.set(n.id, newId);
  });

  const droppedRepeatNodes: Map<string, DroppedRepeatNode> = new Map();

  // Map through the nodes and update their IDs and any references to other node IDs. Also,
  // clear out certain fields that should not be duplicated on certain nodes.
  const sanitizedNodes = nodes
    .map((node) => {
      if (node.type === NodeType.NODE_TYPE_PHONE_TREE) {
        // Duplicated Phone Tree node - clear out instruction and primitive IDs. Update the
        // primitiveName to indicate it's a copy.
        return {
          ...node,
          id: oldIdsToNewIds.get(node.id),
          callObject: {
            ...node.callObject,
            instructionSetId: '',
            instructionId: '',
            primitiveId: '',
            primitiveName: `Copy of ${node.callObject.primitiveName}`,
          },
        };
      } else if (node.type === NodeType.NODE_TYPE_REPEAT) {
        const targetId = node.callObject.repeatExpansion?.phoneTreeNodeId;
        if (targetId && !oldIdsToNewIds.has(targetId)) {
          // This Repeat node points outside this subtree, drop it
          droppedRepeatNodes.set(node.id, {
            id: node.id,
            name: node.callObject?.primitiveName ?? 'Repeat',
          });
          return undefined; // Will filter out later
        } else {
          // If the repeat node was not dropped then it means that it points to a node within the duplicated subtree.
          // We need to update the reference to point to the new node ID of the target phone tree.
          return {
            ...node,
            id: oldIdsToNewIds.get(node.id),
            callObject: {
              ...node.callObject,
              instructionSetId: '',
              instructionId: '',
              primitiveId: '',
              primitiveName: transformRepeatsString(node.callObject.primitiveName),
              repeatExpansion: {
                ...node.callObject.repeatExpansion,
                phoneTreeNodeId: targetId ? oldIdsToNewIds.get(targetId) : '', // Update to new ID
              },
            },
          };
        }
      } else if (node.type === NodeType.NODE_TYPE_TREE_OPTION) {
        // Tree Option nodes - clear out primitiveId
        return {
          ...node,
          id: oldIdsToNewIds.get(node.id) || '',
          callObject: {
            ...node.callObject,
            primitiveId: '',
          },
        };
      }
      // All other nodes - create a new node with updated ID
      return {
        ...node,
        id: oldIdsToNewIds.get(node.id) || '',
      };
    })
    .filter(Boolean);

  // Need to filter out edges that point to dropped Repeat nodes and update the source and target IDs
  // to the new node IDs.
  const updatedEdges = edges
    .map((edge) => {
      if (droppedRepeatNodes.has(edge.sourceId) || droppedRepeatNodes.has(edge.targetId)) {
        // Remove edges for any dropped nodes. These will be filtered out
        return undefined;
      }

      return {
        ...edge,
        sourceId: oldIdsToNewIds.get(edge.sourceId) || '',
        targetId: oldIdsToNewIds.get(edge.targetId) || '',
      };
    })
    .filter(Boolean);

  return {
    sanitized: {
      nodes: sanitizedNodes as CallRouteV1.Types.Node[],
      edges: updatedEdges,
    },
    droppedRepeatNodes: Array.from(droppedRepeatNodes.values()),
  };
};

export type CanvasPhoneTree = {
  name: string;
  onCanvas: true;
  subsequentPhoneTreeCount: number;
  phoneTreeNodeId: string;
  nodes: CallRouteV1.Types.Node[];
  edges: Edge[];
  extensionDialingEnabled: boolean | undefined;
  promptMediaItemId: string;
  callObject: CallRouteV1.Types.PhoneTreeNode['callObject'];
};

/**
 * Extracts phone tree data from canvas nodes and edges.
 *
 * @param canvasNodes - Array of nodes from the call route canvas
 * @param canvasEdges - Array of edges from the call route canvas
 * @returns Array of CanvasPhoneTree objects containing phone tree information
 */
export const getCanvasPhoneTrees = (canvasNodes: CallRouteV1.Types.Node[], canvasEdges: Edge[]): CanvasPhoneTree[] => {
  const phoneTreeNodes = canvasNodes.filter((node) => node.type === NodeType.NODE_TYPE_PHONE_TREE);

  return phoneTreeNodes.map((node) => {
    const baseName = node.callObject?.primitiveName || 'Unnamed Phone Tree';
    const extensionDialingEnabled = node.callObject?.phoneTreeExpansion?.extensionDialing;
    const promptMediaItemId = node.callObject?.phoneTreeExpansion?.promptMediaItemId || '';

    const downstreamNodes = getDescendantNodes({ nodeId: node.id, nodes: canvasNodes, edges: canvasEdges });

    const phoneTreeGraphEdges = canvasEdges.filter((edge) =>
      downstreamNodes.some((n) => n.id === edge.sourceId || n.id === edge.targetId)
    );
    const phoneTreeGraphNodes = [node, ...downstreamNodes];

    const subsequentPhoneTreeCount = phoneTreeGraphNodes.filter(
      (n) => n.id !== node.id && n.type === NodeType.NODE_TYPE_PHONE_TREE
    ).length;

    return {
      name: baseName,
      onCanvas: true,
      subsequentPhoneTreeCount,
      phoneTreeNodeId: node.id,
      nodes: phoneTreeGraphNodes,
      edges: phoneTreeGraphEdges,
      extensionDialingEnabled,
      promptMediaItemId,
      callObject: node.callObject,
    };
  });
};

/**
 * Given a node id, will traverse the graph and check if that node or any of its descendants are phone tree nodes that
 * are being used in overrides.
 *
 * @param nodeId the node id of the node to check for phone tree override usage. This can be any node in the graph, it
 * does not need to be a phone tree node since we will check all descendants of this node as well.
 * @param nodes the nodes in the call route graph.
 * @param edges the edges in the call route graph.
 * @param overrides the list of overrides to check against.
 *
 * @returns a record where the keys are the node ids of the phone tree nodes that are being used in overrides and the
 * values are the node objects along with an array of the overrides that are using that phone tree node.
 */
export const getPhoneTreeOverrideUsage = ({
  nodeId,
  nodes,
  edges,
  overrides,
}: {
  nodeId: string;
  nodes: CallRouteV1.Types.Node[];
  edges: Edge[];
  overrides: OverridesListResponse;
}) => {
  const nodeItself = nodes.find((n) => n.id === nodeId && n.type === CallRouteV1.Types.NodeType.NODE_TYPE_PHONE_TREE);
  // Get all phone tree descendants of the node, including the node itself if it's a phone tree node
  const descendants = getDescendantNodes({ nodeId, nodes, edges }).filter(
    (n) => n.type === CallRouteV1.Types.NodeType.NODE_TYPE_PHONE_TREE
  );

  const nodesWithOverrides: Record<string, CallRouteV1.Types.Node & { overrides: PhoneOverride[] }> = {};

  // Combine both override types and process them together
  const allOverrides = [...overrides.phoneOverrides, ...(overrides.deviceExtensionOverrides ?? [])];

  allOverrides.forEach((override) => {
    let matchingNode: CallRouteV1.Types.Node | undefined;

    // Check if the node itself matches the override's phone tree ID, otherwise check the descendants
    if (nodeItself?.callObject.primitiveId === override.ivrMenuId) {
      matchingNode = nodeItself;
    } else {
      matchingNode = descendants.find((descendant) => descendant.callObject.primitiveId === override.ivrMenuId);
    }

    if (matchingNode) {
      if (!nodesWithOverrides[matchingNode.id]) {
        nodesWithOverrides[matchingNode.id] = { ...matchingNode, overrides: [] };
      }
      nodesWithOverrides[matchingNode.id].overrides.push(override);
    }
  });

  return nodesWithOverrides;
};

/**
 * Determines whether an add or edit action would cause downstream nodes to be removed.
 *
 * For Add: downstream is removed if the extending node has children and the new node type is associative or terminal.
 * For Edit: downstream is removed if the type changes and the current/new type combination causes structural changes.
 *
 * Weave Assistant nodes are treated as non-associative and non-terminal since they preserve downstream steps via
 * their fallback node.
 *
 * @param {Object} params - The parameters for the function.
 * @param {string} params.nodeId - The ID of the node being acted upon (extending node for add, editing node for edit).
 * @param {NodeState} params.newNodeState - The state of the new or updated node.
 * @param {Node[]} params.nodes - The array of all nodes.
 * @param {Edge[]} params.edges - The array of all edges.
 * @param {NodeActionType} params.actionType - The type of action being performed (Add or Edit).
 * @returns {boolean} - Returns `true` if downstream nodes would be removed, otherwise `false`.
 */
export const willRemoveDownstream = ({
  nodeId,
  newNodeState,
  nodes,
  edges,
  actionType,
}: {
  nodeId: string;
  newNodeState: NodeState;
  nodes: CallRouteV1.Types.Node[];
  edges: Edge[];
  actionType: NodeActionType;
}): boolean => {
  const newNodeType = newNodeState.type;
  let newTypeIsAssociative = isAssociativeNode(newNodeType);
  let newNodeIsTerminal = isTerminalNode(newNodeState);

  // Treat Weave Assistant nodes as non-associative and non-terminal since they preserve downstream steps
  // via their fallback node.
  if (newNodeType === CallRouteV1.Types.NodeType.NODE_TYPE_WEAVE_ASSISTANT) {
    newTypeIsAssociative = false;
    newNodeIsTerminal = false;
  }

  if (actionType === NodeAction.Add) {
    return (newTypeIsAssociative || newNodeIsTerminal) && hasDownStreamSteps({ nodeId, nodes, edges });
  }

  if (actionType === NodeAction.Edit) {
    const editingNode = nodes.find((node) => node.id === nodeId);
    if (!editingNode) {
      return false;
    }

    const currentType = editingNode.type;
    const currentIsAssociative =
      isAssociativeNode(currentType) &&
      // Weave Assistant nodes are technically associative since they have a fallback node, but we don't want
      // to treat them as associative for the purposes of this function since they preserve downstream steps.
      currentType !== CallRouteV1.Types.NodeType.NODE_TYPE_WEAVE_ASSISTANT;
    const hasChildren = hasDownStreamSteps({ nodeId, nodes, edges });

    if (
      !hasChildren ||
      // If the node type has not changed, then no need to determine if downstream nodes would be removed unless
      // the type is a forwarding number since the forwarding number node could have changed it's ring duration to
      // "ring until answered" which needs to be considered. It would be considered a terminal node in this case
      // (isTerminalNode function returns true in this case)
      (currentType === newNodeType && newNodeType !== CallRouteV1.Types.NodeType.NODE_TYPE_FORWARD_NUMBER)
    ) {
      return false;
    }

    return (
      (!currentIsAssociative && newTypeIsAssociative) ||
      (currentIsAssociative && !newTypeIsAssociative) ||
      (currentIsAssociative && newTypeIsAssociative) ||
      (!currentIsAssociative && !newTypeIsAssociative && newNodeIsTerminal)
    );
  }

  return false;
};
